/**
 * StepSquadDesigner — Etapa: Squad Designer (Fase 4 · setup).
 *
 * Porte FIEL do Squad Designer do protótipo (NavaFlowPrototype.jsx · DesignerScreen)
 * para o app real, incluindo o drag & drop:
 *   - Painel esquerdo: TEMPLATES DE SQUAD (adiciona squads) + NODE TYPES arrastáveis.
 *   - Canvas: grid de pontos, linhas de conexão (SVG), containers de squad e nós
 *     arrastáveis (reposiciona nós existentes + solta novos tipos da paleta).
 *   - Painel direito: SQUAD CONFIG (nome/missão/agentes/remover) OU o painel do
 *     MEMBRO (agente do catálogo, selo do prompt, Ver/Editar e Gerar prompt de
 *     especialista, modelo, max tokens, perfil de ferramentas, skills, Standards).
 *
 * Agentes especialistas (SPEC A2):
 *   - o design é gravado como `version: 2` com `skills`/`model_origem` por membro e o
 *     mapa `responsaveis`/`responsaveis_origem` (FR-1); o textarea "Prompt extension"
 *     saiu (FR-2) — o texto legado vira prompt do projeto quando o dono decidir;
 *   - o modelo inicial vem do `model` do catálogo (FR-14, DA-3);
 *   - sugestão com `exists=false` aparece como rascunho (FR-5);
 *   - selo de cada membro e o mapa etapa → membro vêm da conferência (FR-6/FR-7/FR-11).
 *   - selo "Especialista"/"Genérico" em toda lista de escolha de agente; na escolha os
 *     especialistas vêm primeiro (prompt especialista antes de incompleto/sem prompt) e
 *     os genéricos por último — papel genérico só no último caso.
 *   - ao carregar, o mapa etapa → membro já vem com o MELHOR agente aplicado em toda etapa
 *     sem responsável aprovado (como um "Aceitar"; o especialista do catálogo / papel
 *     genérico entra no squad), uma vez por carregamento, com aviso; "Escolher outro"
 *     troca. Não salva sozinho: "Salvar mapa" / "Continuar" gravam.
 *   - a lista de membros mostra TODOS (rolagem vertical) e a escolha do agente do nó
 *     oferece o catálogo INTEIRO (useTodosOsAgentes pagina a API até o fim).
 *
 * Diferente do protótipo (que é 100% mockado e reseta ao recarregar), aqui a
 * composição (squads + nós + modelos + posições) PERSISTE em
 * working_state.squad_design (GET/PUT /projects/{id}/squad-design). O grafo visual
 * de RUN (DesignerScreen sobre a execução) segue para ajuste fino antes do Gate A.
 *
 * Paleta C, estilos inline, pt-BR.
 */
import { MOSTRAR_ENGINEERING_STANDARDS } from '@/lib/engineeringStandards';
import { useState, useRef, useEffect, useMemo } from 'react';
import {
  Layers, Code2, FlaskConical, Plus, Circle, Bot, GitBranch, RefreshCw,
  ArrowRight, Trash2, ChevronRight, Cpu, Save, Sparkles, FileText,
} from 'lucide-react';
import { C } from '@/theme/palette';
import { Btn, Badge } from '@/components/ui';
import { LoadingSpinner } from '@/components/ui/QueryState';
import { useJourney } from '../JourneyContext';
import {
  useSquadDesign, useSaveSquadDesign, useTechBlueprint, useSquadConferencia,
} from '@/api/techScope';
import {
  useSuggestAgents, useTodosOsAgentes, useSalvarPrompt, buscarAgenteNoCatalogo,
} from '@/api/agents';
import { useApi } from '@/api/client';
import { SeloPrompt } from './squad/SeloPrompt';
import { PromptEditorModal } from './squad/PromptEditorModal';
import { MapaResponsaveis, responsavelEfetivo } from './squad/MapaResponsaveis';
import { SkillsDoMembro } from './squad/SkillsDoMembro';
import { MemoriaDoMembro } from './squad/MemoriaDoMembro';
import { SeloTipo } from './squad/SeloTipo';
import {
  usd, custoDoModelo, gruposDeMembros, MODELOS, TEXTO_DO_PERFIL, membrosDoDesign, mensagemDoErro, seloDoMembro,
  agruparPorCategoria, descricaoCurta, ordenarPorEspecialidade, prefixoDoTipo, tipoDoAgente, TITULO_DO_TIPO,
} from './squad/comum';

/** Altura máxima da lista de membros: acima dela a lista ROLA (NFR-3: nunca corta). */
const ALTURA_DA_LISTA_DE_MEMBROS = 420;

/** Modelo do catálogo de um AgentOut (`model` ou `default_model_hint`), se válido. */
function modeloDoAgente(a) {
  const m = String(a?.model ?? a?.default_model_hint ?? '').toLowerCase();
  return MODELOS.includes(m) ? m : null;
}

// Converte a sugestão da IA (agentes do catálogo, coerentes com o escopo/blueprint)
// em MÚLTIPLOS squads agrupados por especialidade (Técnico / Dev / Qualidade) —
// espelha a estrutura do protótipo e enche o canvas, mas com agentes reais do
// catálogo (não o seed .NET hardcoded).
function _tplMission(id, fallback) {
  return SQUAD_TEMPLATES.find((t) => t.id === id)?.mission || fallback;
}
function _mkSquad(id, templateId, name, color, mission, members, modelos = {}) {
  return {
    id, templateId, name, color, mission,
    agents: members.map((a, i) => ({
      id: `${id}-a${i}`, role: a.name, label: a.role || a.name,
      // FR-14: o modelo inicial é o do catálogo (sem heurística); trocar vira "membro".
      model: modelos[a.name] || 'sonnet', model_origem: 'catalogo', skills: null,
      // FR-5: fora do catálogo → rascunho VISUAL; o rascunho real nasce no PUT (FR-3).
      ...(a.exists === false ? { status_catalogo: 'rascunho' } : {}),
    })),
  };
}
const _chaveDoAgente = (a) => `${a.name} ${a.role || ''} ${a.category || ''}`.toLowerCase();
const _ehTecnico = (a) => /arch|arquitet|cloud|terraform|devops|infra|\blead\b|organizer/.test(_chaveDoAgente(a));
const _ehQualidade = (a) => /\bqa\b|test|segur|security|audit|qualidade/.test(_chaveDoAgente(a));
/** Template de squad que melhor acolhe o agente (usado ao aceitar um papel padrão). */
function templateDoAgente(a) {
  if (_ehTecnico(a)) return 'tecnico';
  if (_ehQualidade(a)) return 'qualidade';
  return 'dev';
}
function buildSquadsFromAgents(agents, modelos = {}) {
  const list = (agents || []).filter((a) => a && a.name);
  if (!list.length) return null;
  const isTec = _ehTecnico;
  const isQa = _ehQualidade;
  const tec = list.filter(isTec);
  const qa = list.filter((a) => !isTec(a) && isQa(a));
  const dev = list.filter((a) => !isTec(a) && !isQa(a));
  const squads = [];
  if (tec.length) squads.push(_mkSquad('sq-tec', 'tecnico', 'Squad Técnico', C.quantum, _tplMission('tecnico', 'Fundação: arquitetura, infraestrutura e esqueleto do projeto.'), tec, modelos));
  if (dev.length) squads.push(_mkSquad('sq-dev', 'dev', 'Squad de Desenvolvimento', C.cyber, _tplMission('dev', 'Implementa o backlog com a stack decidida no Blueprint.'), dev, modelos));
  if (qa.length) squads.push(_mkSquad('sq-qa', 'qualidade', 'Squad Qualidade', C.neon, _tplMission('qualidade', 'Testes, cobertura e qualidade a cada ciclo.'), qa, modelos));
  return squads.length ? squads : null;
}

// ─── Constantes (portadas do protótipo) ─────────────────────────────────────
const SQUAD_TEMPLATES = [
  { id: 'tecnico', label: 'Técnico', color: C.quantum, icon: Layers,
    mission: 'Fundação: arquitetura, infraestrutura, ADRs e esqueleto do projeto (executa o Ciclo 0).',
    defaultAgents: [
      { role: 'backend-architect', label: 'Backend Architect', model: 'opus', tier: 'ARCHITECT' },
      { role: 'cloud-architect', label: 'Cloud Architect', model: 'opus', tier: 'ARCHITECT' },
      { role: 'terraform-engineer', label: 'Terraform Engineer', model: 'sonnet', tier: 'ENGINEER' },
    ] },
  { id: 'dev', label: 'Desenvolvimento', color: C.cyber, icon: Code2,
    mission: 'Executa o backlog: implementa histórias e atividades técnicas dos ciclos 1..N.',
    defaultAgents: [
      { role: 'csharp-pro', label: 'C# .NET Pro', model: 'sonnet', tier: 'DEV' },
      { role: 'react-pro', label: 'React Pro', model: 'sonnet', tier: 'DEV' },
      { role: 'postgres-pro', label: 'PostgreSQL Pro', model: 'sonnet', tier: 'DEV' },
    ] },
  { id: 'qualidade', label: 'Qualidade', color: C.neon, icon: FlaskConical,
    mission: 'Testes, cobertura e métricas de qualidade a cada ciclo de desenvolvimento.',
    defaultAgents: [
      { role: 'qa-engineer', label: 'QA Engineer', model: 'sonnet', tier: 'QA' },
      { role: 'test-automator', label: 'Test Automator', model: 'sonnet', tier: 'QA' },
      { role: 'security-auditor', label: 'Security Auditor', model: 'sonnet', tier: 'CYBER' },
    ] },
  { id: 'custom', label: 'Custom', color: C.digital, icon: Plus,
    mission: 'Monte do zero a partir do catálogo Nava (394 agents disponíveis).',
    defaultAgents: [] },
];

const SQUADS_INIT = [
  { id: 'sq1', templateId: 'tecnico', name: 'Squad Fundação', color: C.quantum,
    mission: 'Provisiona infra, define arquitetura e entrega o esqueleto do projeto no Ciclo 0.',
    agents: [
      { id: 'a1', role: 'backend-architect', label: 'Backend Architect', model: 'opus', tier: 'ARCHITECT' },
      { id: 'a2', role: 'cloud-architect', label: 'Cloud Architect', model: 'opus', tier: 'ARCHITECT' },
      { id: 'a3', role: 'terraform-engineer', label: 'Terraform Engineer', model: 'sonnet', tier: 'ENGINEER' },
    ] },
  { id: 'sq2', templateId: 'dev', name: 'Squad Core', color: C.cyber,
    mission: 'Implementa o núcleo do produto (backend + dados).',
    agents: [
      { id: 'a4', role: 'csharp-pro', label: 'C# .NET Pro', model: 'sonnet', tier: 'DEV' },
      { id: 'a5', role: 'postgres-pro', label: 'PostgreSQL Pro', model: 'sonnet', tier: 'DEV' },
      { id: 'a6', role: 'backend-architect', label: 'Backend Architect', model: 'opus', tier: 'ARCHITECT' },
    ] },
  { id: 'sq3', templateId: 'qualidade', name: 'Squad Qualidade', color: C.neon,
    mission: 'Valida cada ciclo: testes, cobertura, mutação e compliance.',
    agents: [
      { id: 'a7', role: 'qa-engineer', label: 'QA Engineer', model: 'sonnet', tier: 'QA' },
      { id: 'a8', role: 'test-automator', label: 'Test Automator', model: 'sonnet', tier: 'QA' },
      { id: 'a9', role: 'security-auditor', label: 'Security Auditor', model: 'sonnet', tier: 'CYBER' },
    ] },
];

/** Agente do design v1/v2 → v2 (FR-1): `model_origem` (padrão catálogo) e `skills` (null herda). */
function normalizarAgente(ag) {
  return {
    ...ag,
    model_origem: ag?.model_origem === 'membro' ? 'membro' : 'catalogo',
    skills: Array.isArray(ag?.skills) ? ag.skills : null,
  };
}
const normalizarSquads = (sqs) => (sqs || []).map((sq) => ({ ...sq, agents: (sq.agents || []).map(normalizarAgente) }));
const normalizarNos = (nds) => (nds || []).map((n) => (n?.type === 'agent' ? normalizarAgente(n) : n));

// (Os papéis de agente NÃO são mais uma lista fixa — o NODE CONFIG busca no
// catálogo REAL via useTodosOsAgentes/GET /agents. A sugestão inicial também vem do
// catálogo, via /agents/suggest.)

const NODE_PALETTE = [
  { type: 'input', icon: Circle, label: 'Input', desc: 'Entrada do fluxo' },
  { type: 'agent', icon: Bot, label: 'LLM Agent', desc: 'Agente IA do catálogo' },
  { type: 'condition', icon: GitBranch, label: 'Condition', desc: 'Branching condicional' },
  { type: 'transform', icon: RefreshCw, label: 'Transform', desc: 'Transformação' },
  { type: 'output', icon: ArrowRight, label: 'Output', desc: 'Saída do fluxo' },
];

const SQUAD_COLOR_CYCLE = [C.cyber, C.quantum, C.neon, C.digital, C.hotLight];

const squadColorById = (squads, id) => squads.find((s) => s.id === id)?.color || C.neutralMuted;
const squadContainerH = (sq) => Math.max(80, sq.agents.length * 90 - 10);

function makeNode(type, x, y, id) {
  const base = { id, x, y };
  switch (type) {
    case 'agent': return { ...base, type: 'agent', label: 'Novo Agente', agent: 'backend-architect', model: 'sonnet', model_origem: 'catalogo', skills: null, squad: null, maxTokens: '32000' };
    case 'input': return { ...base, type: 'input', label: 'Input', agent: 'Início do fluxo' };
    case 'output': return { ...base, type: 'output', label: 'Output', agent: 'Saída do fluxo' };
    case 'condition': return { ...base, type: 'condition', label: 'Condição', agent: 'Branching condicional' };
    case 'transform': return { ...base, type: 'transform', label: 'Transform', agent: 'Transformação' };
    default: return { ...base, type, label: type, agent: '' };
  }
}

// ── Organograma (auto-layout hierárquico, NUNCA torto) ────────────────────────
// A orquestração (Input → PO → Tech Lead → Output) fica no eixo central, sempre
// CENTRALIZADA sobre os squads. Cada squad é uma coluna igualmente espaçada; os
// agentes descem verticalmente sob o cabeçalho do squad. Tudo derivado de um único
// `squadGeom`, que se adapta ao nº de squads/agentes — assim, ao montar de novo, o
// desenho continua simétrico independente de quantos agentes cada squad tem.
const COL_W = 280;          // espaçamento horizontal entre squads
const FIRST_X = 220;        // centro da 1ª coluna de squad
const AGENT_Y0 = 380;       // topo (centro do 1º agente) de cada coluna
const AGENT_H = 90;         // passo vertical entre agentes

function squadGeom(squads) {
  const nS = Math.max(1, squads.length);
  const colX = (i) => FIRST_X + i * COL_W;
  const centerX = (colX(0) + colX(nS - 1)) / 2;                 // eixo da orquestração
  const maxAgents = Math.max(1, ...squads.map((s) => (s.agents?.length || 0)));
  const colBottom = (i) => AGENT_Y0 + (Math.max(1, squads[i]?.agents?.length || 1) - 1) * AGENT_H + 20;
  const busBottomY = AGENT_Y0 + (maxAgents - 1) * AGENT_H + 20 + 44;  // barramento sob o squad mais alto
  const outputY = busBottomY + 60;
  const canvasW = Math.max(1180, colX(nS - 1) + FIRST_X + 100);
  const canvasH = outputY + 90;
  return { nS, colX, centerX, maxAgents, colBottom, busBottomY, outputY, canvasW, canvasH };
}

const canvasWidthFor = (squads) => squadGeom(Array.isArray(squads) ? squads : []).canvasW;

// `anteriores`: os nós atuais — a orquestração (PO/Tech Lead) guarda modelo, origem do
// modelo, skills e tokens ao re-layout (adicionar/remover squad não apaga a escolha).
function layoutSquadNodes(squads, anteriores = []) {
  const g = squadGeom(squads);
  const manter = (id) => {
    const n = (anteriores || []).find((x) => x.id === id);
    if (!n) return {};
    const { x: _x, y: _y, ...resto } = n;  // eslint-disable-line no-unused-vars
    return resto;
  };
  const orchestration = [
    { id: 'input', label: 'Input', type: 'input', x: g.centerX, y: 30, agent: 'Início do fluxo' },
    { id: 'po', label: 'Product Owner', type: 'agent', agent: 'product-owner-agent', model: 'sonnet', model_origem: 'catalogo', skills: null, squad: null, ...manter('po'), x: g.centerX, y: 130 },
    { id: 'tl', label: 'Tech Lead', type: 'agent', agent: 'tech-lead-agent', model: 'opus', model_origem: 'catalogo', skills: null, squad: null, ...manter('tl'), x: g.centerX, y: 230 },
  ];
  const agents = squads.flatMap((sq, i) =>
    sq.agents.map((ag, j) => ({
      ...manter(`${sq.id}-${ag.id}`),
      id: `${sq.id}-${ag.id}`, label: ag.label, type: 'agent',
      x: g.colX(i), y: AGENT_Y0 + j * AGENT_H,
      agent: ag.role, model: ag.model, squad: sq.id, agentRef: ag.id,
    })),
  );
  return [
    ...orchestration,
    ...agents,
    { id: 'output', label: 'Pull Request', type: 'output', x: g.centerX, y: g.outputY, agent: 'PR gerado no repositório' },
  ];
}

// ─── Sub-componentes (portados) ──────────────────────────────────────────────
function FormLabel({ children }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 600, color: C.neutralMuted,
      letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>
      {children}
    </div>
  );
}

function SquadContainer({ x, y, w, h, squad, selected, onClick }) {
  const color = squad.color;
  const tpl = SQUAD_TEMPLATES.find((t) => t.id === squad.templateId);
  return (
    <div style={{ position: 'absolute', left: x, top: y, width: w, height: h,
      border: selected ? `1px dashed ${color}` : `1px dashed ${color}44`,
      borderRadius: 12, backgroundColor: selected ? `${color}0D` : `${color}06`,
      pointerEvents: 'none' }}>
      <button onClick={onClick} title={`Configurar ${squad.name}`}
        style={{ position: 'absolute', top: -10, left: 12, pointerEvents: 'auto', cursor: 'pointer',
          backgroundColor: C.black, padding: '2px 8px', border: 'none',
          fontSize: 10, color, letterSpacing: '0.08em', fontWeight: 700 }}>
        {squad.name.toUpperCase()}{tpl ? ` · ${tpl.label.toUpperCase()}` : ''}
      </button>
      <div style={{ position: 'absolute', bottom: -9, right: 12, backgroundColor: C.black,
        padding: '1px 7px', fontSize: 10, color: C.neutralMuted, fontFamily: 'monospace' }}>
        {squad.agents.length} agents
      </div>
    </div>
  );
}

function CanvasNode({ node, squadColor = null, selected, onClick, onDragStart, selo = null, rascunho = false, model, tipo = null }) {
  const isAgent = node.type === 'agent';
  const isIO = node.type === 'input' || node.type === 'output';
  const accentColor = squadColor || (node.type === 'condition' ? C.digital : node.type === 'transform' ? C.neon : node.type === 'agent' ? C.cyber : C.neutral);
  const TypeIcon = isIO ? Circle : node.type === 'condition' ? GitBranch : node.type === 'transform' ? RefreshCw : Bot;
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart && onDragStart(e, node)}
      onClick={onClick}
      style={{ position: 'absolute', left: node.x - 70, top: node.y - 20, width: 140, padding: '10px 12px',
        backgroundColor: C.blackElev,
        border: selected ? `2px solid ${C.cyber}` : `1px solid ${C.blackBorder}`,
        borderLeft: isIO ? `1px solid ${C.blackBorder}` : `3px solid ${accentColor}`,
        borderRadius: 8, cursor: 'grab',
        boxShadow: selected ? `0 0 0 4px ${C.cyber}22` : '0 2px 8px rgba(0,0,0,0.3)',
        transition: 'box-shadow 150ms, border-color 150ms' }}>
      <div className="flex items-center gap-1.5 mb-1">
        <TypeIcon size={10} style={{ color: isIO ? C.neutralDim : accentColor }} />
        <span style={{ fontSize: 12, color: C.neutral, fontWeight: 600, letterSpacing: '-0.01em' }}>{node.label}</span>
      </div>
      <div style={{ fontSize: 10, color: C.neutralMuted, lineHeight: 1.3 }}>{node.agent}</div>
      {isAgent && (
        <div className="flex items-center gap-1 mt-1.5">
          <span style={{ fontSize: 9, color: accentColor, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{model || node.model}</span>
          {selo && (
            <span title={selo === 'especialista' ? 'prompt de especialista' : 'sem prompt de especialista completo'}
              style={{ fontSize: 9, marginLeft: 'auto' }}>{selo === 'especialista' ? '✅' : '⚠️'}</span>
          )}
          {rascunho && (
            <span style={{ fontSize: 8.5, color: C.warn, fontWeight: 700, letterSpacing: '0.06em' }}>RASCUNHO</span>
          )}
          {tipo === 'generico' && (
            <span title={TITULO_DO_TIPO.generico}
              style={{ fontSize: 8.5, color: C.neutralMuted, fontWeight: 700, letterSpacing: '0.06em' }}>GENÉRICO</span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Painel: lista de membros (todos, com rolagem vertical — NFR-3) ───────────
function ListaDeMembros({ membros, confPorAgente, modeloDe, onConfigurar, onAbrirPrompt }) {
  const total = membros.length;
  return (
    <div role="region" aria-label="Membros do squad">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: C.neutralMuted, letterSpacing: '0.06em', fontWeight: 600 }}>
          MEMBROS DO SQUAD ({total})
        </span>
      </div>
      <div data-testid="lista-de-membros-rolavel"
        style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: ALTURA_DA_LISTA_DE_MEMBROS }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }} aria-label="Membros e prompts">
          <tbody>
            {membros.map((m) => {
              const conf = confPorAgente.get(m.agent) ?? null;
              const model = modeloDe(m);
              return (
                <tr key={m.agent} data-testid={`membro-${m.agent}`} style={{ borderTop: `1px solid ${C.blackBorder}` }}>
                  <td style={{ padding: '6px 8px', fontSize: 12.5, color: C.neutral }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {m.label}
                      <SeloTipo tipo={m.tipo} agente={m.agent} testId={`tipo-${m.agent}`} />
                    </span>
                    <div style={{ fontSize: 10.5, color: C.neutralMuted, fontFamily: 'monospace' }}>{m.agent}</div>
                  </td>
                  <td style={{ padding: '6px 8px' }}>
                    <SeloPrompt membro={conf} agent={m.agent} />
                    {!conf && m.rascunho && (
                      <div style={{ fontSize: 10.5, color: C.warn, marginTop: 2 }}>fora do catálogo — vira rascunho ao salvar</div>
                    )}
                  </td>
                  <td style={{ padding: '6px 8px', fontSize: 11, color: C.neutralMuted, fontFamily: 'monospace', textTransform: 'uppercase' }}>
                    {model}
                  </td>
                  <td style={{ padding: '6px 8px', fontSize: 11.5, color: C.neutralDim, whiteSpace: 'nowrap' }}>
                    {usd(custoDoModelo(model))}
                  </td>
                  <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                    <button type="button" onClick={() => onAbrirPrompt(m)} aria-label={`Ver/Editar prompt de ${m.agent}`}
                      style={{ fontSize: 11.5, padding: '3px 8px', borderRadius: 5, marginRight: 6, cursor: 'pointer',
                        background: 'transparent', color: C.cyber, border: `1px solid ${C.cyber}66` }}>
                      Prompt
                    </button>
                    <button type="button" onClick={() => onConfigurar(m)} aria-label={`Configurar ${m.agent}`}
                      style={{ fontSize: 11.5, padding: '3px 8px', borderRadius: 5, cursor: 'pointer',
                        background: 'transparent', color: C.neutralDim, border: `1px solid ${C.blackBorder}` }}>
                      Configurar
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Tela principal ──────────────────────────────────────────────────────────
export function StepSquadDesigner({ onNext, onBack }) {
  const { projectId, deliveryScope } = useJourney();
  const api = useApi();
  const designQ = useSquadDesign(projectId);
  const saveDesign = useSaveSquadDesign(projectId);
  const conferenciaQ = useSquadConferencia(projectId);
  const salvarPrompt = useSalvarPrompt();
  const suggest = useSuggestAgents();
  const blueprintQ = useTechBlueprint(projectId);
  const deliveryType = ['fullstack', 'frontend', 'backend', 'analise'].includes(deliveryScope)
    ? deliveryScope : 'fullstack';

  // Dica de stack a partir do Blueprint decidido (snake_case do GET). Reforça a
  // sugestão para greenfield (onde o repo_stack é vazio) — a IA escolhe agentes
  // coerentes com a stack aprovada no Gate 2, não os defaults do protótipo.
  const stackHint = () => {
    const bp = blueprintQ.data || {};
    const parts = [
      (bp.languages || []).length ? `Linguagens: ${bp.languages.join(', ')}` : '',
      (bp.frameworks || []).length ? `Frameworks (frontend/backend): ${bp.frameworks.join(', ')}` : '',
      (bp.infrastructure || []).length ? `Infraestrutura/Cloud: ${bp.infrastructure.join(', ')}` : '',
      (bp.databases || []).length ? `Bancos: ${bp.databases.join(', ')}` : '',
    ].filter(Boolean).join(' · ');
    return parts
      ? `Stack decidida no Blueprint (AUTORITATIVA — REGRA FIXA: os especialistas de `
        + `FRONTEND, BACKEND, INFRAESTRUTURA/CLOUD e BANCO DE DADOS DEVEM corresponder `
        + `EXATAMENTE a estas tecnologias; NÃO use agentes genéricos nem troque a stack): ${parts}.`
      : '';
  };

  // Catálogo REAL de agentes (GET /agents) — fonte única para formar/trocar o
  // squad. Nada de lista fixa nem internet: só os agentes/skills do catálogo Nava.
  // O catálogo vem INTEIRO (pagina a API de 200 em 200) — nenhum agente fica de fora.
  const [agentSearch, setAgentSearch] = useState('');
  const catalogQ = useTodosOsAgentes({ search: agentSearch.trim() || undefined });
  // Especialistas primeiro (prompt especialista → incompleto → sem prompt); genéricos por último.
  const catalogAgents = useMemo(() => ordenarPorEspecialidade(catalogQ.data?.items ?? []), [catalogQ.data]);
  // Tipo (especialista|generico) que a API já mandou por slug — sugestão e catálogo.
  const [tiposSugeridos, setTiposSugeridos] = useState({});

  // Começa com a estrutura padrão (SQUADS_INIT) — layout do protótipo na hora, nunca
  // vazio/torto. A sugestão do escopo (ou o design salvo) substitui em seguida com
  // os agentes reais da stack.
  const [squads, setSquads] = useState(() => normalizarSquads(SQUADS_INIT));
  const [nodes, setNodes] = useState(() => layoutSquadNodes(normalizarSquads(SQUADS_INIT)));
  const [selectedNode, setSelectedNode] = useState('po');
  const [selectedSquad, setSelectedSquad] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  // Mapa etapa → membro aprovado pelo dono (FR-1/FR-11).
  const [responsaveis, setResponsaveis] = useState({});
  const [responsaveisOrigem, setResponsaveisOrigem] = useState({});
  const [mapaAviso, setMapaAviso] = useState(null);
  // Modelo do catálogo por slug (FR-14) — resolvido uma vez por agente.
  const [modeloCat, setModeloCat] = useState({});
  // Editor de prompt aberto: { nome, rotulo, gerar }.
  const [editor, setEditor] = useState(null);
  // Aba do painel do membro: 'config' (edição) | 'memoria' (só leitura, A6 FR-13)
  const [abaMembro, setAbaMembro] = useState('config');
  // Respostas do PUT v2 (FR-2, FR-3).
  const [legados, setLegados] = useState([]);
  const [rascunhosCriados, setRascunhosCriados] = useState([]);
  const [legadoAviso, setLegadoAviso] = useState(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const seqRef = useRef(1);
  const pedidosRef = useRef(new Set());
  const montadoRef = useRef(true);
  useEffect(() => () => { montadoRef.current = false; }, []);

  const conferencia = conferenciaQ.data && typeof conferenciaQ.data === 'object' ? conferenciaQ.data : null;
  const confPorAgente = useMemo(
    () => new Map((conferencia?.membros || []).filter((m) => m?.agent).map((m) => [m.agent, m])),
    [conferencia],
  );

  // Aplica um conjunto de squads (recalcula os nós do canvas + seleção).
  const applySquads = (next) => {
    setSquads(next);
    const laid = layoutSquadNodes(next, nodes);
    setNodes(laid);
    seqRef.current = laid.length + next.length + 1;
    setSelectedNode('po');
    setSelectedSquad(null);
  };

  // Modelo do catálogo para uma lista de slugs (FR-14). Guarda no cache local.
  const resolverModelos = async (slugs) => {
    const pares = await Promise.all(
      slugs.map(async (s) => [s, modeloDoAgente(await buscarAgenteNoCatalogo(api, s))]),
    );
    const achados = Object.fromEntries(pares.filter(([, m]) => m));
    if (montadoRef.current && Object.keys(achados).length) setModeloCat((prev) => ({ ...prev, ...achados }));
    return achados;
  };

  // Sugere o squad a partir do ESCOPO real (Blueprint + tudo na memória até aqui),
  // via /agents/suggest com o project_id. Como esta etapa roda DEPOIS do Gate 2,
  // a stack já está decidida — a IA escolhe agentes coerentes (ex.: TypeScript, não
  // .NET). Substitui o seed hardcoded do protótipo.
  const suggestFromScope = async () => {
    try {
      const res = await suggest.mutateAsync({
        delivery_type: deliveryType, project_id: projectId, max_agents: 8,
        scope_text: stackHint() || undefined,
      });
      const sugeridos = (res?.suggested || []).filter((a) => a && a.name);
      // Genérico + especialista da mesma camada: nada sai — o genérico só ganha o selo
      // (o mapa etapa → membro já prefere o especialista).
      const tipos = Object.fromEntries(sugeridos.filter((a) => a.tipo === 'especialista' || a.tipo === 'generico')
        .map((a) => [a.name, a.tipo]));
      if (Object.keys(tipos).length && montadoRef.current) setTiposSugeridos((prev) => ({ ...prev, ...tipos }));
      const nomes = sugeridos.filter((a) => a.exists !== false).map((a) => a.name);
      nomes.forEach((n) => pedidosRef.current.add(n));
      const modelos = nomes.length ? await resolverModelos(nomes) : {};
      const built = buildSquadsFromAgents(sugeridos, modelos);
      if (built) { applySquads(built); return true; }
      if (!squads.length) applySquads(normalizarSquads(SQUADS_INIT));  // vazio → fallback genérico
      return false;
    } catch {
      if (!squads.length) applySquads(normalizarSquads(SQUADS_INIT));
      return false;
    }
  };

  // Reidrata do que estiver salvo (uma vez). Sem design salvo → sugere do ESCOPO
  // (IA), caindo no seed dos templates só se a sugestão falhar/vier vazia.
  useEffect(() => {
    if (hydrated || designQ.isLoading) return;
    const d = designQ.data?.design;
    if (d && Array.isArray(d.squads) && Array.isArray(d.nodes) && d.squads.length) {
      setSquads(normalizarSquads(d.squads));
      setNodes(normalizarNos(d.nodes));
      setResponsaveis(d.responsaveis && typeof d.responsaveis === 'object' ? d.responsaveis : {});
      setResponsaveisOrigem(d.responsaveis_origem && typeof d.responsaveis_origem === 'object' ? d.responsaveis_origem : {});
      seqRef.current = Number(d.seq) || (d.nodes.length + d.squads.length + 1);
      setSelectedNode(d.nodes.find((n) => n.id === 'po') ? 'po' : (d.nodes[0]?.id ?? null));
      setHydrated(true);
    } else if (projectId) {
      // best-effort; se falhar, mantém o SQUADS_INIT já no estado
      suggestFromScope().finally(() => setHydrated(true));
    } else {
      setHydrated(true);
    }
  }, [designQ.isLoading, designQ.data, hydrated, projectId]);  // eslint-disable-line react-hooks/exhaustive-deps

  /** Tipo do slug: conferência → sugestão → catálogo → regra local (comum.js). */
  const tipoDoSlug = (slug) => confPorAgente.get(slug)?.tipo
    ?? tiposSugeridos[slug]
    ?? catalogAgents.find((a) => a.name === slug)?.tipo
    ?? tipoDoAgente(slug);

  const membros = useMemo(() => {
    const base = membrosDoDesign(squads, nodes);
    return base.map((m) => {
      const ent = m.squadId
        ? squads.find((sq) => sq.id === m.squadId)?.agents.find((a) => a.id === m.agentId)
        : nodes.find((n) => n.id === m.nodeId);
      const conf = confPorAgente.get(m.agent);
      return {
        ...m, entrada: ent ?? null, rascunho: ent?.status_catalogo === 'rascunho',
        tipo: tipoDoSlug(m.agent),
        prompt_status: conf?.prompt?.status ?? catalogAgents.find((a) => a.name === m.agent)?.prompt_status,
      };
    });
  }, [squads, nodes, confPorAgente, tiposSugeridos, catalogAgents]);  // eslint-disable-line react-hooks/exhaustive-deps
  const chaveDosSlugs = membros.map((m) => m.agent).join('|');

  // Resolve o modelo do catálogo dos membros ainda sem ele (FR-14).
  useEffect(() => {
    const faltam = membros.map((m) => m.agent).filter((s) => !(s in modeloCat) && !pedidosRef.current.has(s));
    if (!faltam.length || !projectId) return;
    faltam.forEach((s) => pedidosRef.current.add(s));
    resolverModelos(faltam).catch(() => {});
  }, [chaveDosSlugs, projectId]);  // eslint-disable-line react-hooks/exhaustive-deps

  /** Modelo efetivo: o do membro se ele trocou; senão o do catálogo (DA-3). */
  const modeloEfetivo = (entrada, slug) => {
    if (entrada?.model_origem === 'membro' && MODELOS.includes(entrada?.model)) return entrada.model;
    return modeloCat[slug] ?? confPorAgente.get(slug)?.model_efetivo ?? entrada?.model ?? 'sonnet';
  };
  const modeloDoMembro = (m) => modeloEfetivo(m.entrada, m.agent);

  const node = nodes.find((n) => n.id === selectedNode) || nodes[0];
  const squad = squads.find((s) => s.id === selectedSquad) || null;
  const g = squadGeom(squads);
  const canvasW = g.canvasW;

  const entradaDoNo = (n) => {
    if (!n) return null;
    if (n.squad && n.agentRef) {
      const ag = squads.find((sq) => sq.id === n.squad)?.agents.find((a) => a.id === n.agentRef);
      if (ag) return ag;
    }
    return n;
  };

  const CAMPOS_ESPELHADOS = ['label', 'model', 'model_origem', 'skills', 'status_catalogo'];
  const updateNode = (id, patch) => {
    setNodes((ns) => ns.map((n) => (n.id === id ? { ...n, ...patch } : n)));
    const nd = nodes.find((n) => n.id === id);
    if (nd?.squad && nd.agentRef) {
      setSquads((sqs) => sqs.map((sq) => sq.id !== nd.squad ? sq : ({
        ...sq,
        agents: sq.agents.map((ag) => {
          if (ag.id !== nd.agentRef) return ag;
          const next = { ...ag };
          for (const k of CAMPOS_ESPELHADOS) if (k in patch) next[k] = patch[k];
          if (patch.agent !== undefined) next.role = patch.agent;
          return next;
        }),
      })));
    }
  };
  const updateSquad = (id, patch) => setSquads((sqs) => sqs.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const addSquad = (tpl) => {
    const id = `sq-n${seqRef.current++}`;
    const sq = {
      id, templateId: tpl.id, name: `Squad ${tpl.label}`,
      color: SQUAD_COLOR_CYCLE[squads.length % SQUAD_COLOR_CYCLE.length],
      mission: tpl.mission,
      agents: tpl.defaultAgents.map((a, i) => normalizarAgente({ ...a, id: `${id}-a${i}` })),
    };
    const next = [...squads, sq];
    setSquads(next);
    setNodes(layoutSquadNodes(next, nodes));
    setSelectedSquad(id);
  };
  const removeSquad = (id) => {
    if (squads.length <= 1) return;
    const next = squads.filter((s) => s.id !== id);
    setSquads(next);
    setNodes(layoutSquadNodes(next, nodes));
    setSelectedSquad(null);
  };

  /**
   * Squads com `slug` incluído como membro (no squad do template que o acolhe; senão o
   * último). Já membro → devolve os mesmos squads. Pura: serve ao Aceitar e ao
   * auto-preenchimento, que inclui vários de uma vez.
   */
  const squadsComMembro = (sqs, nds, slug) => {
    if (membrosDoDesign(sqs, nds).some((m) => m.agent === slug)) return sqs;
    const tpl = templateDoAgente({ name: slug });
    const alvo = sqs.find((sq) => sq.templateId === tpl) || sqs[sqs.length - 1];
    if (!alvo) return sqs;
    const novo = normalizarAgente({
      id: `${alvo.id}-a${seqRef.current++}`, role: slug, label: slug,
      model: modeloCat[slug] || 'sonnet', model_origem: 'catalogo', skills: null,
    });
    return sqs.map((sq) => (sq.id === alvo.id ? { ...sq, agents: [...sq.agents, novo] } : sq));
  };

  /** Inclui um papel padrão aceito no mapa como MEMBRO do squad (FR-9: nunca fallback invisível). */
  const incluirMembro = (slug) => {
    const next = squadsComMembro(squads, nodes, slug);
    if (next === squads) return;
    setSquads(next);
    setNodes(layoutSquadNodes(next, nodes));
  };

  /** Guarda o tipo da proposta (especialista|generico) — o selo segue certo depois que ela entra no squad. */
  const lembrarTipoDaProposta = (etapa, slug) => {
    const e = (conferencia?.etapas || []).find((x) => x?.etapa === etapa);
    if (e?.tipo_proposta && e.proposta === slug) setTiposSugeridos((prev) => ({ [slug]: e.tipo_proposta, ...prev }));
  };

  const escolherResponsavel = (etapa, slug) => {
    setResponsaveis((r) => ({ ...r, [etapa]: slug }));
    setResponsaveisOrigem((o) => ({ ...o, [etapa]: 'aprovado' }));
    setMapaAviso('Mapa alterado — salve para valer no gate.');
  };
  const aceitarResponsavel = (etapa, slug) => {
    lembrarTipoDaProposta(etapa, slug);
    incluirMembro(slug);
    escolherResponsavel(etapa, slug);
  };

  // Melhor agente já aplicado: quando a conferência chega, toda etapa aplicável SEM
  // responsável aprovado e COM proposta recebe a proposta como se o dono tivesse clicado
  // "Aceitar" (especialista do catálogo / papel genérico entram no squad). UMA vez por
  // carregamento — a conferência refeita depois de salvar não reaplica, então troca
  // manual nunca é desfeita, e responsável já aprovado nunca é sobrescrito. Não salva
  // sozinho: o aviso pede para salvar (e o "Continuar" salva).
  const autoPreenchidoRef = useRef(false);
  const [autoAviso, setAutoAviso] = useState(null);
  useEffect(() => {
    if (autoPreenchidoRef.current || !hydrated || !conferencia) return;
    autoPreenchidoRef.current = true;
    let sqs = squads;
    const resp = { ...responsaveis };
    const ro = { ...responsaveisOrigem };
    const tipos = {};
    const aplicadas = [];
    for (const e of conferencia.etapas || []) {
      if (!e || e.aplicavel === false) continue;
      const ef = responsavelEfetivo(e, { responsaveis: resp, responsaveisOrigem: ro, membros: membrosDoDesign(sqs, nodes) });
      if (!ef.slug || ef.origem === 'aprovado') continue;
      sqs = squadsComMembro(sqs, nodes, ef.slug);
      resp[e.etapa] = ef.slug;
      ro[e.etapa] = 'aprovado';
      if (e.tipo_proposta && e.proposta === ef.slug) tipos[ef.slug] = e.tipo_proposta;
      aplicadas.push({ etapa: e.etapa, slug: ef.slug, origem: ef.origem });
    }
    if (!aplicadas.length) return;
    if (sqs !== squads) {
      setSquads(sqs);
      setNodes(layoutSquadNodes(sqs, nodes));
    }
    if (Object.keys(tipos).length) setTiposSugeridos((prev) => ({ ...tipos, ...prev }));
    setResponsaveis(resp);
    setResponsaveisOrigem(ro);
    const antes = new Set(membrosDoDesign(squads, nodes).map((m) => m.agent));
    const entraram = [...new Set(aplicadas.map((a) => a.slug))].filter((sl) => !antes.has(sl));
    const genericos = aplicadas.filter((a) => a.origem === 'papel_padrao').length;
    setAutoAviso({ n: aplicadas.length, entraram, genericos });
  }, [hydrated, conferencia]);  // eslint-disable-line react-hooks/exhaustive-deps

  const onNodeDragStart = (e, n) => {
    const r = e.currentTarget.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - r.left, y: e.clientY - r.top };
    e.dataTransfer.setData('text/plain', 'move:' + n.id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onCanvasDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const data = e.dataTransfer.getData('text/plain');
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    if (data.startsWith('move:')) {
      const id = data.slice(5);
      setNodes((ns) => ns.map((n) => (n.id === id ? { ...n, x: x - dragOffset.current.x + 70, y: y - dragOffset.current.y + 20 } : n)));
    } else if (data.startsWith('new:')) {
      const type = data.slice(4);
      const id = `n-${seqRef.current++}`;
      setNodes((ns) => [...ns, makeNode(type, x, y, id)]);
      setSelectedNode(id);
      setSelectedSquad(null);
    }
  };

  // Design v2 (FR-1): modelo efetivo, origem do modelo, skills e o mapa só com membros.
  const montarDesign = ({
    squads: sqs = squads, nodes: nds = nodes,
    responsaveis: resp = responsaveis, responsaveisOrigem: ro = responsaveisOrigem,
  } = {}) => {
    const squadsOut = sqs.map((sq) => ({
      ...sq,
      agents: sq.agents.map((ag) => ({
        ...ag,
        model: modeloEfetivo(ag, ag.role),
        model_origem: ag.model_origem === 'membro' ? 'membro' : 'catalogo',
        skills: Array.isArray(ag.skills) ? ag.skills : null,
      })),
    }));
    const nodesOut = nds.map((n) => {
      if (n.type !== 'agent') return n;
      const ent = n.squad && n.agentRef
        ? squadsOut.find((sq) => sq.id === n.squad)?.agents.find((a) => a.id === n.agentRef)
        : null;
      if (ent) {
        return { ...n, agent: ent.role, label: ent.label, model: ent.model, model_origem: ent.model_origem, skills: ent.skills };
      }
      return {
        ...n,
        model: modeloEfetivo(n, n.agent),
        model_origem: n.model_origem === 'membro' ? 'membro' : 'catalogo',
        skills: Array.isArray(n.skills) ? n.skills : null,
      };
    });
    const slugs = new Set(membrosDoDesign(squadsOut, nodesOut).map((m) => m.agent));
    const respOut = {};
    const roOut = {};
    for (const [etapa, slug] of Object.entries(resp || {})) {
      if (slug && slugs.has(slug)) {      // EC-1: membro removido → a etapa volta à proposta
        respOut[etapa] = slug;
        roOut[etapa] = ro?.[etapa] || 'aprovado';
      }
    }
    return {
      version: 2, squads: squadsOut, nodes: nodesOut, seq: seqRef.current,
      responsaveis: respOut, responsaveis_origem: roOut,
    };
  };

  const persist = async (overrides) => {
    const out = await saveDesign.mutateAsync(montarDesign(overrides));
    setAutoAviso(null);  // salvo: o preenchimento automático já vale no gate
    setRascunhosCriados(Array.isArray(out?.rascunhos_criados) ? out.rascunhos_criados : []);
    setLegados(Array.isArray(out?.migracao?.prompts_legados) ? out.migracao.prompts_legados : []);
    return out;
  };
  const handleSave = () => { persist().catch(() => {}); };
  const salvarMapa = () => {
    persist().then(() => setMapaAviso('Mapa salvo.')).catch(() => setMapaAviso(null));
  };
  async function handleNext() {
    try { await persist(); } catch { /* não bloqueia a navegação */ }
    onNext && onNext();
  }

  // FR-2: o texto legado da "Prompt extension" vira versão `project` (origem importado).
  const transformarLegado = (item) => {
    setLegadoAviso(null);
    salvarPrompt.mutate(
      { nome: item.agent, texto: item.texto, scope: 'project', projectId, origem: 'importado' },
      {
        onSuccess: (out) => {
          const novos = nodes.map((n) => {
            if (n.agent !== item.agent || !('prompt' in n)) return n;
            const { prompt: _p, ...resto } = n;  // eslint-disable-line no-unused-vars
            return resto;
          });
          setNodes(novos);
          setLegados((l) => l.filter((x) => x.agent !== item.agent));
          setLegadoAviso(`Prompt do projeto criado para ${item.agent} (v${out?.version ?? '?'}).`);
          persist({ nodes: novos }).catch(() => {});
        },
        onError: (err) => setLegadoAviso(mensagemDoErro(err, 'Não foi possível transformar o texto legado.')),
      },
    );
  };

  const totalCost = membros.reduce((a, m) => a + custoDoModelo(modeloDoMembro(m)), 0);
  const squadCostOf = (sq) => sq.agents.reduce((a, ag) => a + custoDoModelo(modeloEfetivo(ag, ag.role)), 0);
  const agentCount = nodes.filter((n) => n.type === 'agent').length;

  const inputStyle = { width: '100%', backgroundColor: C.black, border: `1px solid ${C.blackBorder}`, color: C.neutral, padding: '10px 14px', borderRadius: 6, fontSize: 14, outline: 'none', boxSizing: 'border-box' };

  if (!projectId) return <div style={{ padding: 40, color: C.neutralDim }}>Nenhum projeto selecionado.</div>;
  if (!hydrated && designQ.isLoading) return <LoadingSpinner label="Carregando composição do squad..." />;

  const entradaSel = node?.type === 'agent' ? entradaDoNo(node) : null;
  const slugSel = node?.type === 'agent' ? (entradaSel?.role ?? node.agent) : null;
  const confSel = slugSel ? confPorAgente.get(slugSel) ?? null : null;
  const modeloSel = node?.type === 'agent' ? modeloEfetivo(entradaSel, slugSel) : null;
  const origemModeloSel = entradaSel?.model_origem === 'membro' ? 'membro' : 'catalogo';
  const saveErr = saveDesign.error;
  const slugsInvalidos = Array.isArray(saveErr?.problemBody?.slugs_invalidos) ? saveErr.problemBody.slugs_invalidos : null;

  return (
    <div>
      {/* Cabeçalho */}
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: C.neutral, margin: '0 0 6px' }}>Squad Designer</h2>
        <p style={{ fontSize: 13.5, color: C.neutralDim, margin: 0, maxWidth: '80ch' }}>
          Monte os squads do projeto: adicione templates, arraste blocos para o canvas e
          configure cada membro (agente do catálogo, prompt de especialista, modelo, skills).
          Defina quem responde por cada etapa no mapa abaixo. A composição é salva no projeto;
          o ajuste fino do grafo acontece no RUN, antes do Gate A.
        </p>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, flexWrap: 'wrap', padding: '10px 12px', borderRadius: 8,
        border: `1px solid ${C.blackBorder}`, background: C.blackElev, marginBottom: 12 }}>
        <div className="flex items-center gap-3" style={{ minWidth: 0 }}>
          <div className="flex items-center gap-2">
            <Cpu size={15} style={{ color: C.cyber }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: C.neutral }}>
              {agentCount} agents · {squads.length} squads
            </span>
          </div>
          <Badge color={C.cyber} light>SUGERIDO</Badge>
          <span data-testid="custo-total" style={{ fontSize: 12, color: C.neutralMuted, fontFamily: 'monospace' }}>
            custo est. {usd(totalCost)}
          </span>
          {conferencia?.contract_mode && (
            <Badge color={conferencia.contract_mode === 'strict' ? C.warn : C.neutralMuted} light>
              {conferencia.contract_mode === 'strict' ? 'modo estrito' : 'modo híbrido'}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
          <Btn variant="ghost" size="sm" icon={Sparkles} onClick={() => suggestFromScope()}
            disabled={suggest.isPending}
            title="Sugere o squad a partir do Blueprint e do escopo técnico aprovado">
            {suggest.isPending ? 'Sugerindo…' : 'Sugerir do escopo (IA)'}
          </Btn>
          <Btn variant="outline" size="sm" icon={Save} onClick={handleSave}
            disabled={saveDesign.isPending} aria-label="Salvar composição">
            {saveDesign.isPending ? 'Salvando…' : saveDesign.isSuccess ? '✓ Salvo' : 'Salvar composição'}
          </Btn>
        </div>
      </div>

      {/* Avisos do PUT v2: rascunhos criados (FR-3) e prompts legados (FR-2) */}
      {rascunhosCriados.length > 0 && (
        <div role="status" style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 8, fontSize: 12.5,
          color: C.warn, background: `${C.warn}10`, border: `1px solid ${C.warn}55` }}>
          ⚠️ {rascunhosCriados.length} agente(s) fora do catálogo entraram como <strong>rascunho</strong>, sem
          prompt de especialista: {rascunhosCriados.join(', ')}.
        </div>
      )}
      {legados.length > 0 && (
        <div role="region" aria-label="Prompts legados" style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 8,
          background: C.blackElev, border: `1px solid ${C.blackBorder}` }}>
          <div style={{ fontSize: 12.5, color: C.neutral, fontWeight: 600, marginBottom: 6 }}>
            Texto da antiga “Prompt extension” guardado no design ({legados.length})
          </div>
          <div style={{ fontSize: 12, color: C.neutralDim, marginBottom: 8 }}>
            Esse campo não chegava à execução. Transforme-o em prompt do projeto (fica versionado e
            auditado) — enquanto você não decidir, ele segue guardado no design.
          </div>
          {legados.map((item) => (
            <div key={item.agent} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '6px 0',
              borderTop: `1px solid ${C.blackBorder}` }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <code style={{ fontSize: 12, color: C.cyber }}>{item.agent}</code>
                <div style={{ fontSize: 11.5, color: C.neutralMuted, whiteSpace: 'pre-wrap', marginTop: 2 }}>
                  {String(item.texto || '').slice(0, 240)}{String(item.texto || '').length > 240 ? '…' : ''}
                </div>
              </div>
              <Btn size="sm" variant="outline" icon={FileText} onClick={() => transformarLegado(item)}
                disabled={salvarPrompt.isPending}>
                Transformar em prompt do projeto
              </Btn>
            </div>
          ))}
        </div>
      )}
      {legadoAviso && (
        <div role="status" style={{ marginBottom: 12, fontSize: 12.5, color: C.cyber }}>{legadoAviso}</div>
      )}

      {/* Corpo em 3 colunas */}
      <div style={{ position: 'relative', display: 'flex', height: 620, minHeight: 0, border: `1px solid ${C.blackBorder}`,
        borderRadius: 10, overflow: 'hidden', background: C.black }}>
        {/* Esquerda — templates + node types */}
        <div style={{ width: 220, flexShrink: 0, borderRight: `1px solid ${C.blackBorder}`, padding: 16, overflow: 'auto' }}>
          <div style={{ fontSize: 11, color: C.neutralMuted, letterSpacing: '0.06em', marginBottom: 6, fontWeight: 600 }}>TEMPLATES DE SQUAD</div>
          <div style={{ fontSize: 11, color: C.neutralDim, marginBottom: 12 }}>Adicione quantos squads precisar</div>
          {SQUAD_TEMPLATES.map((tpl) => (
            <div key={tpl.id} style={{ padding: '10px 12px', borderRadius: 6, border: `1px solid ${tpl.color}33`, backgroundColor: `${tpl.color}08`, marginBottom: 8 }}>
              <div className="flex items-center gap-2 mb-1">
                <tpl.icon size={14} style={{ color: tpl.color }} />
                <span style={{ color: C.neutral, fontSize: 13, fontWeight: 500, flex: 1 }}>{tpl.label}</span>
                <button onClick={() => addSquad(tpl)} title={`Adicionar squad ${tpl.label}`}
                  style={{ width: 20, height: 20, borderRadius: 5, border: `1px solid ${tpl.color}66`, backgroundColor: 'transparent', color: tpl.color, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                  <Plus size={12} />
                </button>
              </div>
              <div style={{ fontSize: 10, color: C.neutralDim, lineHeight: 1.4 }}>{tpl.mission}</div>
              <div style={{ fontSize: 10, color: C.neutralMuted, marginTop: 4 }}>{tpl.defaultAgents.length} agents padrão</div>
            </div>
          ))}

          <div style={{ height: 1, backgroundColor: C.blackBorder, margin: '14px 0' }} />
          <div style={{ fontSize: 11, color: C.neutralMuted, letterSpacing: '0.06em', marginBottom: 6, fontWeight: 600 }}>NODE TYPES</div>
          <div style={{ fontSize: 11, color: C.neutralDim, marginBottom: 12 }}>Arraste um bloco para o canvas →</div>
          {NODE_PALETTE.map((it) => (
            <div key={it.label} draggable
              onDragStart={(e) => { e.dataTransfer.setData('text/plain', 'new:' + it.type); e.dataTransfer.effectAllowed = 'copy'; }}
              style={{ width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 6,
                border: `1px solid ${C.blackBorder}`, backgroundColor: C.blackElev, marginBottom: 8, cursor: 'grab' }}>
              <div className="flex items-center gap-2 mb-1">
                <it.icon size={14} style={{ color: C.cyber }} />
                <span style={{ color: C.neutral, fontSize: 13, fontWeight: 500 }}>{it.label}</span>
              </div>
              <div style={{ fontSize: 11, color: C.neutralDim }}>{it.desc}</div>
            </div>
          ))}
        </div>

        {/* Centro — canvas */}
        <div style={{ flex: 1, position: 'relative', overflow: 'auto' }}>
          <div
            onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
            onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(false); }}
            onDrop={onCanvasDrop}
            style={{ position: 'relative', width: canvasW, height: g.canvasH,
              backgroundImage: `radial-gradient(circle, ${C.blackBorder} 1px, transparent 1px)`,
              backgroundSize: '24px 24px',
              outline: dragOver ? `2px dashed ${C.cyber}66` : 'none', outlineOffset: -4 }}>
            <svg style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} width={canvasW} height={g.canvasH}>
              {/* Espinha central: Input → PO → Tech Lead (eixo centralizado) */}
              <line x1={g.centerX} y1="60" x2={g.centerX} y2="130" stroke={C.blackBorder} strokeWidth="1.5" />
              <line x1={g.centerX} y1="170" x2={g.centerX} y2="230" stroke={C.blackBorder} strokeWidth="1.5" />
              {/* Barramento horizontal do Tech Lead até as colunas (topo) */}
              {g.nS > 1 && (
                <line x1={g.colX(0)} y1="320" x2={g.colX(g.nS - 1)} y2="320" stroke={C.blackBorder} strokeWidth="1.5" />
              )}
              {/* Barramento horizontal das colunas até o Output (base) */}
              {g.nS > 1 && (
                <line x1={g.colX(0)} y1={g.busBottomY} x2={g.colX(g.nS - 1)} y2={g.busBottomY} stroke={C.blackBorder} strokeWidth="1.5" />
              )}
              {squads.map((sq, i) => {
                const lx = g.colX(i);
                const bottom = g.colBottom(i);
                return (
                  <g key={sq.id}>
                    {/* TL → barramento topo → topo da coluna */}
                    <path d={`M ${g.centerX} 270 L ${g.centerX} 320 L ${lx} 320 L ${lx} 360`} stroke={C.blackBorder} strokeWidth="1.5" fill="none" />
                    {/* base da coluna → barramento base → Output */}
                    <path d={`M ${lx} ${bottom} L ${lx} ${g.busBottomY} L ${g.centerX} ${g.busBottomY} L ${g.centerX} ${g.outputY - 20}`} stroke={C.blackBorder} strokeWidth="1.5" fill="none" />
                  </g>
                );
              })}
            </svg>

            {squads.map((sq, i) => (
              <SquadContainer key={sq.id} x={g.colX(i) - 90} y={350} w={180} h={squadContainerH(sq)}
                squad={sq} selected={selectedSquad === sq.id}
                onClick={() => setSelectedSquad(sq.id)} />
            ))}

            {nodes.map((n) => {
              const ent = n.type === 'agent' ? entradaDoNo(n) : null;
              const slug = ent?.role ?? n.agent;
              const conf = n.type === 'agent' ? confPorAgente.get(slug) : null;
              return (
                <CanvasNode key={n.id} node={n}
                  squadColor={n.squad ? squadColorById(squads, n.squad) : null}
                  selected={n.id === selectedNode && !selectedSquad}
                  selo={conf ? seloDoMembro(conf) : null}
                  rascunho={ent?.status_catalogo === 'rascunho' || conf?.status_catalogo === 'rascunho'}
                  model={n.type === 'agent' ? modeloEfetivo(ent, slug) : undefined}
                  tipo={n.type === 'agent' ? tipoDoSlug(slug) : null}
                  onClick={() => { setSelectedNode(n.id); setSelectedSquad(null); }}
                  onDragStart={onNodeDragStart} />
              );
            })}
          </div>
        </div>

        {/* Direita — squad config OU painel do membro */}
        <div style={{ width: 340, flexShrink: 0, borderLeft: `1px solid ${C.blackBorder}`, padding: 20, overflow: 'auto' }}>
          {squad ? (
            <>
              <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: C.neutralMuted, letterSpacing: '0.06em', fontWeight: 600 }}>SQUAD CONFIG</div>
                <Badge color={squad.color} light>{SQUAD_TEMPLATES.find((t) => t.id === squad.templateId)?.label || 'Custom'}</Badge>
              </div>
              <FormLabel>Nome do squad</FormLabel>
              <input value={squad.name} onChange={(e) => updateSquad(squad.id, { name: e.target.value })} style={inputStyle} />
              <div style={{ marginTop: 16 }}>
                <FormLabel>Missão</FormLabel>
                <textarea rows={3} value={squad.mission} onChange={(e) => updateSquad(squad.id, { mission: e.target.value })}
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
              <div style={{ marginTop: 16, padding: 12, backgroundColor: C.blackElev, borderRadius: 6, border: `1px solid ${C.blackBorder}` }}>
                <div style={{ fontSize: 11, color: C.neutralMuted, letterSpacing: '0.04em', marginBottom: 8 }}>AGENTES ({squad.agents.length})</div>
                {squad.agents.map((ag) => (
                  <div key={ag.id} className="flex items-center gap-2" style={{ marginBottom: 6 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: squad.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: C.neutral, flex: 1 }}>{ag.label}</span>
                    <span style={{ fontSize: 10, color: C.neutralMuted, textTransform: 'uppercase', fontFamily: 'monospace' }}>{modeloEfetivo(ag, ag.role)}</span>
                  </div>
                ))}
                <div style={{ fontSize: 11, color: C.neutralMuted, marginTop: 8 }}>
                  Custo estimado do squad: {usd(squadCostOf(squad))}
                </div>
              </div>
              <div style={{ marginTop: 16 }}>
                <Btn variant="danger" size="sm" icon={Trash2} onClick={() => removeSquad(squad.id)} disabled={squads.length <= 1}>
                  Remover squad
                </Btn>
                {squads.length <= 1 && (
                  <div style={{ fontSize: 11, color: C.neutralMuted, marginTop: 6 }}>A execução exige ao menos 1 squad.</div>
                )}
              </div>
            </>
          ) : node ? (
            <>
              <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: C.neutralMuted, letterSpacing: '0.06em', fontWeight: 600 }}>
                  {node.type === 'agent' ? 'MEMBRO DO SQUAD' : 'NODE CONFIG'}
                </div>
                <Badge color={C.cyber} light>{node.type}</Badge>
              </div>
              {node.type === 'agent' && (
                <div role="tablist" aria-label="Painel do membro" className="flex gap-2" style={{ marginBottom: 14 }}>
                  {[['config', 'Configuração'], ['memoria', 'Memória']].map(([id, rotulo]) => (
                    <button key={id} type="button" role="tab" aria-selected={abaMembro === id}
                      onClick={() => setAbaMembro(id)}
                      style={{ flex: 1, padding: '6px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        backgroundColor: abaMembro === id ? `${C.cyber}22` : C.blackElev,
                        color: abaMembro === id ? C.cyber : C.neutralDim,
                        border: abaMembro === id ? `1px solid ${C.cyber}` : `1px solid ${C.blackBorder}`,
                        borderRadius: 6 }}>
                      {rotulo}
                    </button>
                  ))}
                </div>
              )}
              {node.type === 'agent' && abaMembro === 'memoria' ? (
                <MemoriaDoMembro agent={slugSel} projectId={projectId} />
              ) : (<>
              <FormLabel>Label</FormLabel>
              <input value={node.label} onChange={(e) => updateNode(node.id, { label: e.target.value })} style={inputStyle} />

              {node.type === 'agent' && (
                <>
                  <div style={{ marginTop: 16 }}>
                    <FormLabel>Agente do catálogo (obrigatório)</FormLabel>
                    <input value={agentSearch} onChange={(e) => setAgentSearch(e.target.value)}
                      placeholder="Buscar no catálogo Nava (nome, categoria)…"
                      style={{ ...inputStyle, marginBottom: 8 }} />
                    <div style={{ position: 'relative' }}>
                      <select value={node.agent} aria-label="Agente do catálogo"
                        onChange={(e) => updateNode(node.id, {
                          agent: e.target.value, label: e.target.value,
                          model_origem: 'catalogo', skills: null, status_catalogo: undefined,
                        })}
                        style={{ ...inputStyle, appearance: 'none', paddingRight: 36, cursor: 'pointer' }}>
                        {/* mantém o agente atual visível mesmo se estiver fora da página filtrada */}
                        {node.agent && !catalogAgents.some((a) => a.name === node.agent) && (
                          <option value={node.agent}>{prefixoDoTipo({ tipo: tipoDoSlug(node.agent) })}{node.agent} (selecionado)</option>
                        )}
                        {/* agrupado por categoria, com o que o agente faz — só o nome induzia ao erro */}
                        {agruparPorCategoria(catalogAgents).map(({ categoria, agentes }) => (
                          <optgroup key={categoria} label={`${categoria} (${agentes.length})`}>
                            {agentes.map((a) => (
                              <option key={a.id || a.name} value={a.name} data-tipo={tipoDoAgente(a)}
                                title={`${TITULO_DO_TIPO[tipoDoAgente(a)]}${a.description ? `\n${descricaoCurta(a.description, 300)}` : ''}`}>
                                {prefixoDoTipo(a)}{a.name}
                                {a.description ? ` — ${descricaoCurta(a.description)}` : ''}
                                {a.prompt_status === 'incompleto' ? ' · prompt incompleto'
                                  : a.prompt_status === 'sem_prompt' ? ' · sem prompt' : ''}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                      <ChevronRight size={14} style={{ position: 'absolute', right: 12, top: 42, color: C.neutralDim, transform: 'rotate(90deg)', pointerEvents: 'none' }} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                      <SeloTipo tipo={tipoDoSlug(node.agent)} testId="tipo-agente-selecionado" />
                      <span style={{ fontSize: 11, color: C.neutralMuted }}>
                        Especialistas primeiro; papel genérico só no último caso.
                      </span>
                    </div>
                    {catalogQ.isLoading ? (
                      <div style={{ fontSize: 11.5, color: C.neutralMuted, marginTop: 6 }}>Carregando catálogo…</div>
                    ) : catalogQ.data && (
                      <div data-testid="total-do-catalogo" style={{ fontSize: 11, color: C.neutralMuted, marginTop: 4 }}>
                        {catalogAgents.length.toLocaleString('pt-BR')} agente(s) {agentSearch.trim() ? 'encontrados' : 'no catálogo'} em {agruparPorCategoria(catalogAgents).length.toLocaleString('pt-BR')} categoria(s) — todos na lista (role para ver).
                      </div>
                    )}
                    {(() => {
                      const sel = catalogAgents.find((a) => a.name === node.agent);
                      if (!sel?.description) return null;
                      return (
                        <div style={{ fontSize: 12, color: C.neutralDim, lineHeight: 1.4, marginTop: 8 }}>{sel.description}</div>
                      );
                    })()}
                    {entradaSel?.status_catalogo === 'rascunho' && !confSel && (
                      <div style={{ fontSize: 11.5, color: C.warn, marginTop: 8 }}>
                        ⚠️ Fora do catálogo — entra como rascunho, sem prompt de especialista, ao salvar.
                      </div>
                    )}
                  </div>

                  {/* Prompt de especialista (FR-7, FR-13) */}
                  <div style={{ marginTop: 16, padding: 12, backgroundColor: C.blackElev, borderRadius: 6, border: `1px solid ${C.blackBorder}` }}>
                    <FormLabel>Prompt de especialista</FormLabel>
                    <SeloPrompt membro={confSel} agent={slugSel} />
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                      <Btn variant="ghost" size="sm" icon={FileText}
                        onClick={() => setEditor({ nome: slugSel, rotulo: node.label, gerar: false })}>
                        Ver/Editar prompt
                      </Btn>
                      <Btn variant="outline" size="sm" icon={Sparkles}
                        onClick={() => setEditor({ nome: slugSel, rotulo: node.label, gerar: true })}>
                        Gerar prompt especialista
                      </Btn>
                    </div>
                    {node.prompt && String(node.prompt).trim() && (
                      <div style={{ fontSize: 11, color: C.neutralMuted, marginTop: 8 }}>
                        Há texto da antiga “Prompt extension” guardado neste nó — ao salvar a composição você
                        poderá transformá-lo em prompt do projeto.
                      </div>
                    )}
                  </div>

                  <div style={{ marginTop: 16 }}>
                    <FormLabel>Modelo LLM</FormLabel>
                    <div className="flex gap-2" role="group" aria-label="Modelo LLM">
                      {MODELOS.map((m) => (
                        <button key={m} type="button" aria-pressed={m === modeloSel}
                          onClick={() => updateNode(node.id, { model: m, model_origem: 'membro' })}
                          style={{ flex: 1, padding: '8px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                            backgroundColor: m === modeloSel ? `${C.cyber}22` : C.blackElev,
                            color: m === modeloSel ? C.cyber : C.neutralDim,
                            border: m === modeloSel ? `1px solid ${C.cyber}` : `1px solid ${C.blackBorder}`,
                            borderRadius: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {m}
                        </button>
                      ))}
                    </div>
                    <div data-testid="origem-modelo" style={{ fontSize: 11, color: C.neutralMuted, marginTop: 6 }}>
                      {origemModeloSel === 'membro'
                        ? `Trocado neste projeto · custo ${usd(custoDoModelo(modeloSel))}`
                        : `Modelo do catálogo · custo ${usd(custoDoModelo(modeloSel))}`}
                      {origemModeloSel === 'membro' && (
                        <button type="button"
                          onClick={() => updateNode(node.id, {
                            model: modeloCat[slugSel] ?? confSel?.model_efetivo ?? modeloSel, model_origem: 'catalogo',
                          })}
                          style={{ marginLeft: 8, background: 'none', border: 'none', color: C.cyber, cursor: 'pointer', fontSize: 11, padding: 0 }}>
                          Voltar ao modelo do catálogo
                        </button>
                      )}
                    </div>
                  </div>

                  <div style={{ marginTop: 16 }}>
                    <FormLabel>Max tokens</FormLabel>
                    <input value={node.maxTokens !== undefined ? node.maxTokens : '32000'}
                      onChange={(e) => updateNode(node.id, { maxTokens: e.target.value.replace(/[^0-9]/g, '') })}
                      style={inputStyle} />
                  </div>

                  {/* Perfil de ferramentas — só leitura (FR-16) */}
                  <div style={{ marginTop: 16 }}>
                    <FormLabel>Perfil de ferramentas</FormLabel>
                    <div data-testid="perfil-ferramentas" style={{ fontSize: 12.5, color: C.neutral }}>
                      {confSel?.tool_profile
                        ? <><code>{confSel.tool_profile}</code> — {TEXTO_DO_PERFIL[confSel.tool_profile] ?? confSel.tool_profile}</>
                        : <span style={{ color: C.neutralMuted }}>— (salve a composição para conferir)</span>}
                    </div>
                    <div style={{ fontSize: 11, color: C.neutralMuted, marginTop: 4 }}>Editável no Catálogo.</div>
                  </div>

                  {/* Skills do membro (FR-15) */}
                  <div style={{ marginTop: 16, padding: 12, backgroundColor: C.blackElev, borderRadius: 6, border: `1px solid ${C.blackBorder}` }}>
                    <SkillsDoMembro agent={slugSel} skills={entradaSel?.skills ?? null}
                      conferidas={confSel?.skills || []}
                      onChange={(skills) => updateNode(node.id, { skills })} />
                  </div>

                  {MOSTRAR_ENGINEERING_STANDARDS && (
                  <div style={{ marginTop: 16, padding: 12, backgroundColor: C.blackElev, borderRadius: 6, border: `1px solid ${C.blackBorder}` }}>
                    <div style={{ fontSize: 11, color: C.neutralMuted, letterSpacing: '0.04em', marginBottom: 6 }}>ENGINEERING STANDARDS APLICÁVEIS</div>
                    <div className="flex flex-wrap gap-1">
                      {['OOP+SOLID', 'DDD', 'TDD', 'CLEAN', 'TOGAF'].map((s) => (<Badge key={s} color={C.cyber} light>{s}</Badge>))}
                    </div>
                  </div>
                  )}
                </>
              )}

              {node.type !== 'agent' && (
                <div style={{ marginTop: 16, padding: 12, backgroundColor: C.blackElev, borderRadius: 6, border: `1px solid ${C.blackBorder}`, fontSize: 12, color: C.neutralDim, lineHeight: 1.5 }}>
                  Node do tipo <strong style={{ color: C.neutral }}>{node.type}</strong>. Edite o label acima e arraste-o para reposicionar no canvas.
                </div>
              )}
              </>)}
            </>
          ) : (
            <div style={{ fontSize: 12.5, color: C.neutralMuted }}>Selecione um nó ou um squad no canvas.</div>
          )}
        </div>
      </div>

      {saveDesign.isError && (
        <div role="alert" style={{ marginTop: 10, fontSize: 12.5, color: C.hot }}>
          {slugsInvalidos?.length
            ? `Nome(s) de agente inválido(s): ${slugsInvalidos.join(', ')}. Use letras minúsculas, números e hífen.`
            : mensagemDoErro(saveErr, 'Falha ao salvar a composição. Tente novamente.')}
        </div>
      )}

      {/* Membros, selos e mapa etapa → membro */}
      <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16 }}>
        <div style={{ padding: 14, borderRadius: 10, border: `1px solid ${C.blackBorder}`, background: C.blackElev }}>
          {conferenciaQ.isError && (
            <div style={{ fontSize: 12, color: C.neutralMuted, marginBottom: 8 }}>
              Conferência do squad indisponível agora — os selos aparecem quando ela responder.
            </div>
          )}
          <ListaDeMembros membros={membros} confPorAgente={confPorAgente} modeloDe={modeloDoMembro}
            onConfigurar={(m) => {
              setSelectedSquad(null);
              setSelectedNode(m.nodeId ?? `${m.squadId}-${m.agentId}`);
            }}
            onAbrirPrompt={(m) => setEditor({ nome: m.agent, rotulo: m.label, gerar: false })} />
        </div>
        <div style={{ padding: 14, borderRadius: 10, border: `1px solid ${C.blackBorder}`, background: C.blackElev }}>
          {(conferencia?.alertas || []).filter((a) => a?.codigo === 'blueprint_nao_aprovado').map((a) => (
            <div key={a.codigo} style={{ fontSize: 12, color: C.warn, marginBottom: 8 }}>⚠️ {a.mensagem}</div>
          ))}
          {autoAviso && (
            <div role="status" data-testid="aviso-auto-preenchimento"
              style={{ marginBottom: 10, padding: '8px 10px', borderRadius: 8, fontSize: 12.5, lineHeight: 1.45,
                color: C.cyber, background: `${C.cyber}10`, border: `1px solid ${C.cyber}55` }}>
              ✨ {autoAviso.n} etapa(s) preenchida(s) com o melhor agente — troque em “Escolher outro” se
              precisar. <strong>Salve para valer no gate.</strong>
              {autoAviso.entraram.length > 0 && (
                <div style={{ color: C.neutralDim, marginTop: 3 }}>
                  Entraram no squad: {autoAviso.entraram.join(', ')}.
                </div>
              )}
              {autoAviso.genericos > 0 && (
                <div style={{ color: C.warn, marginTop: 3 }}>
                  ⚠️ {autoAviso.genericos} etapa(s) com papel genérico — nenhum especialista cobre; último caso.
                </div>
              )}
            </div>
          )}
          <MapaResponsaveis etapas={conferencia?.etapas || []} membros={membros} grupos={gruposDeMembros(squads, nodes)}
            responsaveis={responsaveis} responsaveisOrigem={responsaveisOrigem}
            onEscolher={escolherResponsavel} onAceitar={aceitarResponsavel}
            rodape={(
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                <Btn size="sm" variant="outline" icon={Save} onClick={salvarMapa} disabled={saveDesign.isPending}>
                  Salvar mapa
                </Btn>
                {mapaAviso && <span style={{ fontSize: 12, color: C.neutralDim }}>{mapaAviso}</span>}
              </div>
            )} />
        </div>
      </div>

      {editor && (
        <PromptEditorModal nome={editor.nome} rotulo={editor.rotulo} projectId={projectId}
          gerarAoAbrir={editor.gerar} onClose={() => setEditor(null)} />
      )}

      {/* Navegação do wizard */}
      <div className="flex items-center justify-between" style={{ marginTop: 24, paddingTop: 16, borderTop: `1px solid ${C.blackBorder}` }}>
        <Btn variant="ghost" size="md" onClick={onBack}>Voltar</Btn>
        <Btn variant="primary" size="lg" onClick={handleNext} disabled={saveDesign.isPending}>
          {saveDesign.isPending ? 'Salvando…' : 'Continuar →'}
        </Btn>
      </div>
    </div>
  );
}
